import { z } from 'zod';

/**
 * The length rule for every surface where a user *sets* a password —
 * registration and reset-edit — kept in one place so the two can't drift into
 * different minimums inside one product.
 *
 * 6 rather than 5: the support text shown under the field promises "Your
 * password must be 6 to 128 characters long", and the copy is the promise.
 *
 * This is deliberately stricter than the server, which accepts 5
 * (`rb/services/web/app/models/human.rb` `validates_length_of :password`). The
 * client turning away a password the server would take is the safe direction:
 * the reverse would surface as a server error on a form that said it was fine.
 *
 * @public
 */
export const MIN_PASSWORD_LENGTH = 6;

/** @public */
export const MAX_PASSWORD_LENGTH = 128;

const PASSWORD_LENGTH_ERROR = `Password must be ${MIN_PASSWORD_LENGTH} to ${MAX_PASSWORD_LENGTH} characters`;

/** @public */
export const PASSWORD_BLANK_ERROR = 'Password cannot be only spaces';

export const PASSWORD_SUPPORT_TEXT = `Your password must be ${MIN_PASSWORD_LENGTH} to ${MAX_PASSWORD_LENGTH} characters long.`;

/**
 * The rule for a password field, which a host merges into its own schema —
 * `z.object({ ...yourFields, password: passwordSchema })`.
 *
 * Length is measured on the raw value, not a trimmed one: interior and edge
 * whitespace are legitimate password characters and the server stores what it is
 * sent, so trimming here would silently register a different credential than the
 * one the user typed. The non-blank rule only rules out a value that is *nothing
 * but* whitespace, which no one chooses deliberately and which reads to the user
 * as an empty field.
 */
export const passwordSchema = z
  .string()
  .min(MIN_PASSWORD_LENGTH, PASSWORD_LENGTH_ERROR)
  .max(MAX_PASSWORD_LENGTH, PASSWORD_LENGTH_ERROR)
  .refine((value) => value.trim().length > 0, PASSWORD_BLANK_ERROR);
